import * as React from 'react';

import {getDripEditionStatusInfo} from 'shells/edition/get-edition-status-info';
import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {ErrorState} from 'shells/error-state/error-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Table} from 'shells/table/table';
import {
    convertSortByToUrlSortParam,
    convertUrlSortParamToTableSortBy,
} from 'shells/table/table-sort-helpers';
import type {SortBy} from 'shells/table/types';

import type {DripSequenceStubFragment as DripSequence} from 'nutshell-graphql-types';

import {EmptyState} from './empty-state';

import styles from './drip-sequences-list-view.module.css';

type Props = {
    isLoading: boolean;
    error: string | null | undefined;
    dripSequences: DripSequence[];
    sortString: string;
    isFiltering: boolean;
    isShowingArchived: boolean;
    handleCreateNewDripSequence: () => any;
    onChangeSort: (newSortString?: string | null | undefined) => void;
};

export function DripSequencesListView(props: Props) {
    const columns = React.useMemo(
        () => [
            {
                id: 'name',
                Header: 'Name',
                accessor: (dripSequence: DripSequence) => dripSequence,
                dataType: 'linkedName',
            },
            {
                id: 'status',
                Header: 'Status',
                accessor: (dripSequence: DripSequence) =>
                    getDripEditionStatusInfo(
                        dripSequence.deletedTime ? 'ARCHIVED' : dripSequence.status
                    ),
                dataType: 'statusBadge',
            },
            {
                id: 'messages',
                Header: '# Messages',
                accessor: (dripSequence: DripSequence) => dripSequence.editions.totalCount,
                dataType: 'string',
                minWidth: 120,
                width: 120,
                maxWidth: 120,
            },
            {
                id: 'openRate',
                Header: 'Open rate',
                accessor: (dripSequence: DripSequence) => dripSequence.engagementStats.openRate,
                dataType: 'percentage',
                minWidth: 90,
                width: 90,
                maxWidth: 110,
            },
            {
                id: 'clickRate',
                Header: 'Click rate',
                accessor: (dripSequence: DripSequence) => dripSequence.engagementStats.clickRate,
                dataType: 'percentage',
                minWidth: 90,
                width: 90,
                maxWidth: 110,
            },
            {
                id: 'replyRate',
                Header: 'Reply rate',
                accessor: (dripSequence: DripSequence) => dripSequence.engagementStats.replyRate,
                dataType: 'percentage',
                minWidth: 90,
                width: 90,
                maxWidth: 110,
            },
            {
                id: 'bounceRate',
                Header: 'Bounce rate',
                accessor: (dripSequence: DripSequence) =>
                    dripSequence.engagementStats.permanentBounceRate,
                hasRightBorder: true,
                dataType: 'percentage',
                minWidth: 100,
                width: 100,
                maxWidth: 110,
            },
            {
                id: 'lastSentTime',
                Header: 'Last sent',
                accessor: (dripSequence: DripSequence) =>
                    dripSequence.engagementStats.latestDeliveryTime,
                dataType: 'timestamp',
            },
            {
                id: 'createdTime',
                Header: 'Date created',
                accessor: (dripSequence: DripSequence) => dripSequence.createdTime,
                dataType: 'timestamp',
                minWidth: 150,
                width: 200,
                maxWidth: 200,
            },
            {
                id: 'modifiedTime',
                Header: 'Last updated',
                accessor: (dripSequence: DripSequence) => dripSequence.modifiedTime,
                dataType: 'timestamp',
                minWidth: 150,
                width: 200,
                maxWidth: 200,
                hasRightBorder: true,
            },
        ],
        []
    );

    const handleSortByChange = (newSortBy: SortBy[]) => {
        const newSortString = convertSortByToUrlSortParam(newSortBy);

        props.onChangeSort(newSortString);
    };

    return (
        <div className={styles['drip-sequences-stats']}>
            <Table
                isResizable={true}
                hasHeaderBorder={true}
                hasHeaderCellBorders={true}
                hasHoverState={false}
                isCompact={true}
                stickyHeaders={true}
                hasBorders={true}
                isCheckboxSelectable={false}
                clickingSelectsRow={false}
                disableFilters={true}
                disableSorting={false}
                // Removes "triple sort" and forces there
                // always to be a asc/desc sort applied
                disableSortRemove={true}
                sortBy={convertUrlSortParamToTableSortBy(props.sortString)}
                onChangeSortBy={handleSortByChange}
                shouldPreventSelectAll={false}
                // @ts-expect-error
                columns={columns}
                data={props.dripSequences}
                specialStates={[
                    {
                        shouldRender: () => Boolean(props.error),
                        component: <ErrorState errorMessage={props.error} />,
                    },
                    {
                        shouldRender: () => props.isLoading,
                        component: <LoadingPage padding={0} />,
                    },
                    {
                        shouldRender: () =>
                            !props.dripSequences.length &&
                            !props.isLoading &&
                            (props.isFiltering || props.isShowingArchived),
                        component: (
                            <NothingFoundEmptyState
                                title='No drip sequences were found'
                                subtitle='Try loosening your search!'
                            />
                        ),
                    },
                    {
                        shouldRender: () => !props.dripSequences.length && !props.isLoading,
                        component: (
                            <EmptyState onPrimaryCtaClick={props.handleCreateNewDripSequence} />
                        ),
                    },
                ]}
            />
        </div>
    );
}
